import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

const MAX_MARQUEE_COPIES = 2;

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  /** @type {IntersectionObserver | null} */
  #visibilityObserver = null;

  /** @type {boolean} */
  #initialized = false;

  connectedCallback() {
    super.connectedCallback();

    this.setAttribute('data-initializing', '');

    this.#visibilityObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.#visibilityObserver?.unobserve(this);
          void this.#initialize();
        }
      },
      { rootMargin: '100px 0px' }
    );

    this.#visibilityObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#visibilityObserver?.disconnect();
    this.#visibilityObserver = null;
    window.removeEventListener('resize', this.#handleResize);
    this.removeEventListener('pointerenter', this.#slowDown);
    this.removeEventListener('pointerleave', this.#speedUp);
  }

  async #initialize() {
    if (this.#initialized) return;
    this.#initialized = true;

    const { marqueeItems } = this.refs;
    if (marqueeItems.length === 0) {
      this.removeAttribute('data-initializing');
      return;
    }

    const { numberOfCopies } = await this.#queryNumberOfCopies();

    this.#addRepeatedItems(numberOfCopies);
    this.#duplicateContent();
    this.#setLoopDistance();
    this.#setSpeed(this.#calculateSpeed());

    this.removeAttribute('data-initializing');

    window.addEventListener('resize', this.#handleResize);
    this.addEventListener('pointerenter', this.#slowDown);
    this.addEventListener('pointerleave', this.#speedUp);
  }

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;

  /**
   * @type {number | null}
   */
  #marqueeWidth = null;

  #slowDown = debounce(() => {
    if (this.#animation) return;

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp() {
    this.#slowDown.cancel();

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation || animation.playbackRate === 1) return;

    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  /**
   * @param {number} value
   */
  #setSpeed(value) {
    this.style.setProperty('--marquee-speed', `${Math.max(value, 1)}s`);
  }

  #waitForLayout() {
    return new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    });
  }

  #getMarqueeGap() {
    const gap = parseFloat(getComputedStyle(this).getPropertyValue('--marquee-gap'));
    return Number.isFinite(gap) ? gap : 0;
  }

  /**
   * @param {HTMLElement} marqueeItem
   */
  #measureItemSetWidth(marqueeItem) {
    return marqueeItem.scrollWidth || marqueeItem.getBoundingClientRect().width;
  }

  /**
   * @param {number} containerWidth
   * @param {number} itemSetWidth
   * @param {number} gap
   */
  #calculateNumberOfCopies(containerWidth, itemSetWidth, gap) {
    if (containerWidth <= 0 || itemSetWidth <= 0) return 1;

    let copies = Math.max(1, Math.ceil((containerWidth + gap) / (itemSetWidth + gap)));

    while (copies * itemSetWidth + (copies - 1) * gap < containerWidth) {
      copies += 1;
    }

    return copies;
  }

  async #queryNumberOfCopies() {
    const { marqueeItems } = this.refs;

    if (!marqueeItems[0]) {
      return { numberOfCopies: 1, isHorizontalResize: true };
    }

    await this.#waitForLayout();

    const containerWidth = this.offsetWidth;
    const itemSetWidth = this.#measureItemSetWidth(marqueeItems[0]);
    const gap = this.#getMarqueeGap();

    const isHorizontalResize = this.#marqueeWidth !== containerWidth;
    this.#marqueeWidth = containerWidth;

    const numberOfCopies = Math.min(
      MAX_MARQUEE_COPIES,
      this.#calculateNumberOfCopies(containerWidth, itemSetWidth, gap)
    );

    return { numberOfCopies, isHorizontalResize };
  }

  #setLoopDistance() {
    const { content } = this.refs;
    const contentWidth = content.scrollWidth || content.getBoundingClientRect().width;
    this.style.setProperty('--marquee-content-width', `${contentWidth}px`);
  }

  #calculateSpeed() {
    const speedFactor = Number(this.getAttribute('data-speed-factor')) || 25;
    const contentWidth = this.refs.content?.scrollWidth || 1000;
    return (contentWidth / 1000) * speedFactor;
  }

  #handleResize = debounce(async () => {
    const { marqueeItems } = this.refs;
    const { numberOfCopies, isHorizontalResize } = await this.#queryNumberOfCopies();

    // opt out of marquee manipulation on vertical resizes
    if (!isHorizontalResize) return;

    const currentNumberOfCopies = this.refs.content.children.length;

    if (numberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(numberOfCopies - currentNumberOfCopies);
    } else if (numberOfCopies < currentNumberOfCopies) {
      this.#removeRepeatedItems(currentNumberOfCopies - numberOfCopies);
    }

    this.#duplicateContent();
    this.#setLoopDistance();
    this.#setSpeed(this.#calculateSpeed());
    this.#restartAnimation();
  }, 250);

  #restartAnimation() {
    const animations = this.refs.wrapper.getAnimations();

    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    // ACC: aria-hidden clones must not contain focusable descendants (LH aria-hidden-focus).
    clone.setAttribute('aria-hidden', 'true');
    clone.inert = true;
    clone.removeAttribute('ref');
    clone.querySelectorAll('[ref]').forEach((element) => element.removeAttribute('ref'));
    clone.querySelectorAll('a[href], button, [tabindex]').forEach((element) => {
      if (element instanceof HTMLAnchorElement) {
        element.removeAttribute('href');
      }
      if (element instanceof HTMLElement) {
        element.setAttribute('tabindex', '-1');
      }
    });

    this.refs.wrapper.appendChild(clone);
  }

  /**
   * @param {number} numberOfCopies
   */
  #addRepeatedItems(numberOfCopies) {
    const { content, marqueeItems } = this.refs;

    if (!marqueeItems[0]) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = marqueeItems[0].cloneNode(true);
      clone.removeAttribute('ref');
      content.appendChild(clone);
    }
  }

  /**
   * @param {number} numberOfCopies
   */
  #removeRepeatedItems(countToRemove) {
    const { content } = this.refs;
    let removed = 0;

    while (removed < countToRemove && content.children.length > 1) {
      content.lastElementChild?.remove();
      removed += 1;
    }
  }
}

// Define the animateValue function
/**
 * Animate a numeric property smoothly.
 * @param {Object} params - The parameters for the animation.
 * @param {number} params.from - The starting value.
 * @param {number} params.to - The ending value.
 * @param {number} params.duration - The duration of the animation in milliseconds.
 * @param {function(number): void} params.onUpdate - The function to call on each update.
 * @param {function(number): number} [params.easing] - The easing function.
 * @param {function(): void} [params.onComplete] - The function to call when the animation completes.
 */
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime - The current time in milliseconds.
   */
  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
